import React, { memo, useCallback } from "react";

import {
  Box,
  Button,
  Center,
  Grid,
  GridItem,
  IconButton,
} from "@chakra-ui/react";
import colors from "app/colors";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { CloseIcon, ExternalIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { MessageThreadType } from "gql/graphql";
import { IQuillEditorHandle } from "../compose/quill-message-composer";

interface IProps {
  draftId: string;
  type: MessageThreadType;
  title: string;
  quillEditorRef: React.RefObject<IQuillEditorHandle>;
}

const ComposeMessageThreadHeader = ({
  draftId,
  type,
  title,
  quillEditorRef,
}: IProps) => {
  const { closeDrawer } = useOverlay();
  const { draft, isExternal, createDraft } = useMessageThreadDraft({
    draftId,
  });

  const _clearComposeDraft = useCallback(() => {
    createDraft({ type, draftType: "new-thread" }, true);
    quillEditorRef.current?.reset();
  }, [createDraft, quillEditorRef, type]);

  if (!draft) {
    return null;
  }

  return (
    <Grid
      templateColumns="repeat(3, 1fr)"
      backgroundColor={isExternal ? colors.blueGreen10 : colors.white}
      h="30px"
      mt={4}
    >
      <GridItem>
        <IconButton
          icon={<CloseIcon />}
          aria-label="close"
          variant="ghost"
          size="xs"
          onClick={closeDrawer}
        />
      </GridItem>
      <GridItem>
        <Center w="full" fontWeight={700} fontFamily="Outfit" fontSize={26}>
          {title}
          {isExternal && (
            <Box ml={2}>
              <ExternalIcon fill={colors.transparent} />
            </Box>
          )}
        </Center>
      </GridItem>
      <GridItem textAlign="right">
        <Button
          variant="outline"
          color={colors.gray40}
          borderColor={colors.gray40}
          fontSize={16}
          h={8}
          borderRadius={8}
          onClick={_clearComposeDraft}
        >
          Clear
        </Button>
      </GridItem>
    </Grid>
  );
};

export default memo(ComposeMessageThreadHeader);
